import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { AWARD_ICONS } from '../data/standoutsConstants';
import StandoutShareModal from '../components/StandoutShareModal';
import '../styles/StandoutDetail.css';

const StandoutDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { getStandoutById, trackStandoutAnalytics } = useAuth();

  const [standout, setStandout] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const cardRef = useRef(null);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.width = '100%';
    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
      // Prevent Safari from restoring pre-lock scroll offset on unmount
      window.scrollTo(0, 0);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setNotFound(false);
      const result = await getStandoutById(id);
      if (cancelled) return;
      if (!result.success || !result.data) {
        setNotFound(true);
        setLoading(false);
        return;
      }
      setStandout(result.data);
      trackStandoutAnalytics(id, 'view');
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [id, getStandoutById]);

  // Dynamically update OG meta tags so link previews show the athlete image
  useEffect(() => {
    if (!standout) return;

    const setMeta = (property, content) => {
      let el = document.querySelector(`meta[property="${property}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute('property', property);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    const prevTitle = document.title;
    document.title = `${standout.athlete_name} — ${standout.week_label || 'Standout'} | LegacyStat`;
    setMeta('og:title', `${standout.athlete_name} — Standout of the Week`);
    setMeta('og:description', `${standout.athlete_name} earned ${standout.award || 'Standout'} this week on LegacyStat!`);
    setMeta('og:image', standout.photo_url || '');
    setMeta('og:url', window.location.href);
    setMeta('og:type', 'website');

    return () => {
      document.title = prevTitle;
    };
  }, [standout]);

  if (loading) {
    return (
      <div className="sd-page">
        <div className="sd-back-btn" style={{ visibility: 'hidden' }}>← Back to Standouts</div>
        <div className="sd-card sd-card-skel">
          <div className="sd-skel sd-skel-header" />
          <div className="sd-skel sd-skel-photo" />
          <div className="sd-skel-body">
            <div className="sd-skel sd-skel-name" />
            <div className="sd-skel sd-skel-sub" />
            <div className="sd-skel sd-skel-divider" />
            <div className="sd-skel-stats">
              <div className="sd-skel sd-skel-stat" />
              <div className="sd-skel sd-skel-stat" />
              <div className="sd-skel sd-skel-stat" />
            </div>
            <div className="sd-skel sd-skel-divider" />
            <div className="sd-skel sd-skel-btn" />
          </div>
        </div>
      </div>
    );
  }

  if (notFound || !standout) {
    return (
      <div className="sd-page">
        <div className="sd-not-found">
          <p>Standout not found.</p>
          <button onClick={() => navigate('/standouts')}>← Back to Standouts</button>
        </div>
      </div>
    );
  }

  const iconSrc = standout.award ? AWARD_ICONS[standout.award] : null;
  const weekHeading = standout.week_label
    ? `${standout.week_label.toUpperCase()} STANDOUT`
    : 'STANDOUT';
  const weekDate = standout.week_date_range
    ? `Week of ${standout.week_date_range}`
    : '';
  const orgShort = (standout.org_short_name || standout.org_name || '').split(' ').slice(0, 2).join(' ');

  return (
    <div className="sd-page">

      {/* Back */}
      <button className="sd-back-btn" onClick={() => navigate('/standouts')}>
        ← Back to Standouts
      </button>

      {/* Card */}
      <div className="sd-card" ref={cardRef}>

        {/* ── Card Header ── */}
        <div className="sd-card-header">
          <div className="sd-week-heading-row">
            <span className="sd-heading-star">★</span>
            <h1 className="sd-week-heading">{weekHeading}</h1>
            <span className="sd-heading-star">★</span>
          </div>
          <div className="sd-header-divider">
            <span className="sd-header-line" />
            <span className="sd-header-star">★</span>
            <span className="sd-header-line" />
          </div>
          <div className="sd-header-sub">
            <span className="sd-sub-side sd-sub-left">{standout.season || ''}</span>
            <span className="sd-header-dot">•</span>
            <button
              className="sd-sub-side sd-sub-right sd-org-header-link"
              onClick={() => navigate(`/standouts/org/${standout.org_slug}`)}
            >
              {orgShort}
            </button>
          </div>
        </div>

        {/* ── Photo ── */}
        <div className="sd-photo-wrap">
          <img src={standout.photo_url} alt={standout.athlete_name} className="sd-photo" crossOrigin="anonymous" />
          <div className="sd-photo-gradient" />
        </div>

        {/* ── Body ── */}
        <div className="sd-body">
          <h2 className="sd-name">{(standout.athlete_name || '').toUpperCase()}</h2>

          {standout.award && (
            <div className="sd-award-row">
              <span className="sd-award-line" />
              <div className="sd-award-badge">
                <span className="sd-award-text">{standout.award.toUpperCase()}</span>
              </div>
              <span className="sd-award-line sd-award-line--right" />
            </div>
          )}

          <p className="sd-subtitle">
            <span className="sd-subtitle-gold">{standout.team_name}</span>
            <span className="sd-dot"> • </span>
            {standout.sport}
            {standout.level && (
              <>
                <span className="sd-dot"> • </span>
                <span>{standout.level}</span>
              </>
            )}
            {standout.division && (
              <>
                <span className="sd-dot"> • </span>
                <span className="sd-subtitle-gold">{standout.division}</span>
              </>
            )}
          </p>

          <div className="sd-divider" />

          {weekDate && <p className="sd-date">{weekDate}</p>}

          <div className="sd-divider" />

          {/* Stats */}
          <div className="sd-stats-row">
            {(standout.stats || []).slice(0, 3).map((stat, i) => (
              <React.Fragment key={i}>
                {i > 0 && <div className="sd-stat-sep" />}
                <div className="sd-stat">
                  <span className="sd-stat-value">{stat.value}</span>
                  <span className="sd-stat-label">{stat.label}</span>
                </div>
              </React.Fragment>
            ))}
          </div>

          <div className="sd-divider" />

          {/* Share */}
          <button className="sd-share-btn" onClick={() => setShowShareModal(true)}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"/>
              <polyline points="16 6 12 2 8 6"/>
              <line x1="12" y1="2" x2="12" y2="15"/>
            </svg>
            SHARE
          </button>

          {/* Location */}
          <div className="sd-location">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
              <circle cx="12" cy="10" r="3"/>
            </svg>
            {standout.org_location || '—'}
          </div>
        </div>


      </div>

      {showShareModal && (
        <StandoutShareModal
          standout={standout}
          cardRef={cardRef}
          onCardSaved={(url) => setStandout((prev) => ({ ...prev, card_image_url: url }))}
          onClose={() => setShowShareModal(false)}
        />
      )}
    </div>
  );
};

export default StandoutDetail;
